import React, { useState, useEffect } from 'react';
import {
    BellOutlined,
    BugOutlined,
    CheckCircleOutlined,
    CloseOutlined,
    RocketOutlined,
    ProjectOutlined,
    MessageOutlined,
    NotificationOutlined,
} from '@ant-design/icons';

const getNotificationBannerIcon = (type) => {
    if (!type) {
        return {
            icon: <BellOutlined style={{ color: '#7c3aed', fontSize: 18 }} />,
            bg: '#f3e8ff',
            border: '#d8b4fe',
        };
    }
    const t = type.toLowerCase();
    if (t.includes('bug') || t.includes('report')) {
        return {
            icon: <BugOutlined style={{ color: '#dc2626', fontSize: 18 }} />,
            bg: '#fee2e2',
            border: '#fca5a5',
        };
    }
    if (t.includes('done') || t.includes('resolved') || t.includes('complete')) {
        return {
            icon: <CheckCircleOutlined style={{ color: '#16a34a', fontSize: 18 }} />,
            bg: '#dcfce7',
            border: '#86efac',
        };
    }
    if (t.includes('milestone') || t.includes('sprint') || t.includes('rocket')) {
        return {
            icon: <RocketOutlined style={{ color: '#2563eb', fontSize: 18 }} />,
            bg: '#dbeafe',
            border: '#93c5fd',
        };
    }
    if (t.includes('project')) {
        return {
            icon: <ProjectOutlined style={{ color: '#0284c7', fontSize: 18 }} />,
            bg: '#e0f2fe',
            border: '#7dd3fc',
        };
    }
    if (t.includes('chat') || t.includes('message')) {
        return {
            icon: <MessageOutlined style={{ color: '#059669', fontSize: 18 }} />,
            bg: '#d1fae5',
            border: '#6ee7b7',
        };
    }
    return {
        icon: <NotificationOutlined style={{ color: '#7c3aed', fontSize: 18 }} />,
        bg: '#f3e8ff',
        border: '#d8b4fe',
    };
};

export const NotificationBannerHost = () => {
    const [banners, setBanners] = useState([]);

    useEffect(() => {
        const handleNewNotification = (event) => {
            const detail = event.detail;
            if (!detail) return;

            const id = detail.id || `banner-${Date.now()}-${Math.random()}`;
            const bannerItem = {
                id,
                title: detail.title || 'New Notification',
                message: detail.message || detail.body || '',
                type: detail.type || 'info',
                createdAt: Date.now(),
            };

            setBanners((prev) => [bannerItem, ...prev].slice(0, 3));

            setTimeout(() => {
                setBanners((prev) => prev.filter((b) => b.id !== id));
            }, 5500);
        };

        window.addEventListener('app_notification_received', handleNewNotification);
        return () => window.removeEventListener('app_notification_received', handleNewNotification);
    }, []);

    if (banners.length === 0) return null;

    return (
        <div
            style={{
                position: 'fixed',
                top: 24,
                right: 24,
                zIndex: 2147483647,
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                pointerEvents: 'none',
            }}
        >
            {banners.map((b) => {
                const { icon, bg, border } = getNotificationBannerIcon(b.type);
                return (
                    <div
                        key={b.id}
                        style={{
                            pointerEvents: 'auto',
                            width: 360,
                            backgroundColor: '#ffffff',
                            borderRadius: 14,
                            border: `1.5px solid ${border}`,
                            boxShadow: '0 20px 40px -10px rgba(124, 58, 237, 0.22), 0 8px 20px -4px rgba(0, 0, 0, 0.1)',
                            padding: '14px 16px',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 12,
                            position: 'relative',
                            overflow: 'hidden',
                            animation: 'bannerSlideInRight 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
                        }}
                    >
                        {/* Icon Container */}
                        <div
                            style={{
                                width: 40,
                                height: 40,
                                borderRadius: 11,
                                background: bg,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                marginTop: 1,
                            }}
                        >
                            {icon}
                        </div>

                        {/* Text Block */}
                        <div style={{ flex: 1, minWidth: 0, paddingRight: 16 }}>
                            <div style={{ fontWeight: 700, fontSize: 13.5, color: '#0f172a', letterSpacing: '-0.2px' }}>
                                {b.title}
                            </div>
                            {b.message && (
                                <div
                                    style={{
                                        fontSize: 12,
                                        color: '#475569',
                                        marginTop: 3,
                                        lineHeight: 1.4,
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {b.message}
                                </div>
                            )}
                        </div>

                        {/* Close button */}
                        <button
                            type="button"
                            onClick={() => setBanners((prev) => prev.filter((item) => item.id !== b.id))}
                            style={{
                                position: 'absolute',
                                top: 10,
                                right: 10,
                                background: 'transparent',
                                border: 'none',
                                color: '#94a3b8',
                                cursor: 'pointer',
                                padding: 4,
                                borderRadius: 6,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.color = '#0f172a')}
                            onMouseLeave={(e) => (e.currentTarget.style.color = '#94a3b8')}
                        >
                            <CloseOutlined style={{ fontSize: 13 }} />
                        </button>

                        {/* Animated Timer Progress Line */}
                        <div
                            style={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                height: 3,
                                background: 'linear-gradient(90deg, #7c3aed, #a855f7)',
                                animation: 'bannerTimerShrink 5.5s linear forwards',
                            }}
                        />
                    </div>
                );
            })}
            <style>{`
                @keyframes bannerSlideInRight {
                    from {
                        transform: translateX(120%);
                        opacity: 0;
                    }
                    to {
                        transform: translateX(0);
                        opacity: 1;
                    }
                }
                @keyframes bannerTimerShrink {
                    from {
                        width: 100%;
                    }
                    to {
                        width: 0%;
                    }
                }
            `}</style>
        </div>
    );
};

export default NotificationBannerHost;
